import { useTranslation } from '@portals-platform/i18n';
import {
  Alert,
  AlertDescription,
  Button,
  PageHeader,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@portals-platform/ui';
import { ArrowLeftIcon, CheckCircle2Icon, PencilIcon, RefreshCwIcon } from 'lucide-react';
import type { CertificateDetails } from '../../data-access/index.js';
import { ServerErrorAlert } from '../server-error-alert.js';
import { CertificateDetailsContent } from './certificate-details-content.js';
import { formatLongDate } from './certificate-format.js';
import { CertificateStatusBanner } from './certificate-status-banner.js';

interface CertificateDetailsViewProps {
  certificate: CertificateDetails;
  canEdit: boolean;
  canRequestRenewal: boolean;
  isRequestingRenewal: boolean;
  renewalRequested: boolean;
  renewalError: Error | null;
  onBack: () => void;
  onEdit: () => void;
  onRequestRenewal: () => void;
}

export function CertificateDetailsView({
  certificate,
  canEdit,
  canRequestRenewal,
  isRequestingRenewal,
  renewalRequested,
  renewalError,
  onBack,
  onEdit,
  onRequestRenewal,
}: CertificateDetailsViewProps) {
  const { t } = useTranslation();

  const editButton = (
    <Button size="sm" disabled={!canEdit} onClick={onEdit}>
      <PencilIcon aria-hidden="true" />
      {t('certificates.details.edit')}
    </Button>
  );

  const requestRenewalButton = (
    <Button
      size="sm"
      disabled={!canRequestRenewal || isRequestingRenewal}
      onClick={onRequestRenewal}
    >
      {isRequestingRenewal ? (
        <Spinner className="size-4" aria-hidden="true" />
      ) : (
        <RefreshCwIcon aria-hidden="true" />
      )}
      {t('certificates.details.requestRenewal')}
    </Button>
  );

  return (
    <>
      <PageHeader
        title={certificate.templateName || t('certificates.details.fallbackTitle')}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeftIcon aria-hidden="true" />
              {t('certificates.details.backToCertificates')}
            </Button>
            {canEdit ? (
              editButton
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{editButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>{t('certificates.details.editDisabledTooltip')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {canRequestRenewal ? (
              requestRenewalButton
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{requestRenewalButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('certificates.details.requestRenewalDisabledTooltip')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-6">
        {renewalRequested && (
          <Alert>
            <CheckCircle2Icon aria-hidden="true" />
            <AlertDescription>{t('certificates.details.renewalRequested')}</AlertDescription>
          </Alert>
        )}
        <ServerErrorAlert
          title={t('certificates.details.renewalError')}
          error={renewalError ?? undefined}
        />

        <CertificateStatusBanner
          status={certificate.currentStatus}
          issuedOn={formatLongDate(certificate.createdAt)}
          issuedBy={certificate.createdBy}
        />
        <CertificateDetailsContent certificate={certificate} />
      </div>
    </>
  );
}
